// src/utils/colorUtils.js

// Color palette for courses
const courseColorPalette = [
    'bg-blue-100 border-blue-300 text-blue-900',
    'bg-red-100 border-red-300 text-red-900',
    'bg-indigo-100 border-indigo-300 text-indigo-900',
    'bg-teal-100 border-teal-300 text-teal-900',
    'bg-pink-100 border-pink-300 text-pink-900',
    'bg-cyan-100 border-cyan-300 text-cyan-900',
    'bg-lime-100 border-lime-300 text-lime-900',
    'bg-fuchsia-100 border-fuchsia-300 text-fuchsia-900',
    // Add more colors if needed
];

// Simple hash function for string to number conversion
function hashCode(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash |= 0; // Convert to 32bit integer
    }
    return Math.abs(hash);
}

/**
 * Gets a consistent color class string based on the course name.
 * @param {string} courseName - The name of the course.
 * @returns {string} - Tailwind classes for background, border, and text color.
 */
export function getColorForCourse(courseName) {
    if (!courseName) return courseColorPalette[0]; // Default color
    const hash = hashCode(courseName);
    const index = hash % courseColorPalette.length;
    return courseColorPalette[index];
}

// --- Placeholder mapping functions for Canvas (Keep these here or move if preferred) ---

/**
 * Maps Tailwind background color classes to hex/rgb values for Canvas.
 * @param {string} twClass - Tailwind class string (e.g., 'bg-blue-100 border-blue-300 text-blue-900').
 * @returns {string} - CSS color value (e.g., '#DBEAFE') or a default.
 */
export function mapTailwindBgToColor(twClass) {
    if (!twClass) return '#EFF6FF'; // Default background
    if (twClass.includes('bg-blue-100')) return '#DBEAFE';
    if (twClass.includes('bg-red-100')) return '#FEE2E2';
    if (twClass.includes('bg-indigo-100')) return '#E0E7FF';
    if (twClass.includes('bg-teal-100')) return '#CCFBF1';
    if (twClass.includes('bg-pink-100')) return '#FCE7F3';
    if (twClass.includes('bg-cyan-100')) return '#CFFAFE';
    if (twClass.includes('bg-lime-100')) return '#ECFCCB';
    if (twClass.includes('bg-fuchsia-100')) return '#FAE8FF';
    if (twClass.includes('bg-green-100')) return '#D1FAE5';
    if (twClass.includes('bg-yellow-100')) return '#FEF3C7';
    if (twClass.includes('bg-purple-100')) return '#EDE9FE';
    if (twClass.includes('bg-orange-100')) return '#FFEDD5';
    if (twClass.includes('bg-gray-100')) return '#F3F4F6';
    return '#EFF6FF'; // Default light blue
}

/**
 * Maps Tailwind border color classes to hex/rgb values for Canvas.
 * @param {string} twClass - Tailwind class string.
 * @returns {string} - CSS color value or a default.
 */
export function mapTailwindBorderToColor(twClass) {
     if (!twClass) return '#BFDBFE'; // Default border
     if (twClass.includes('border-blue-300')) return '#93C5FD';
     if (twClass.includes('border-red-300')) return '#FCA5A5';
     if (twClass.includes('border-indigo-300')) return '#A5B4FC';
     if (twClass.includes('border-teal-300')) return '#5EEAD4';
     if (twClass.includes('border-pink-300')) return '#F9A8D4';
     if (twClass.includes('border-cyan-300')) return '#7DD3FC';
     if (twClass.includes('border-lime-300')) return '#BEF264';
     if (twClass.includes('border-fuchsia-300')) return '#F0ABFC';
     if (twClass.includes('border-green-300')) return '#6EE7B7';
     if (twClass.includes('border-yellow-300')) return '#FDE047';
     if (twClass.includes('border-purple-300')) return '#C4B5FD';
     if (twClass.includes('border-orange-300')) return '#FED7AA';
     if (twClass.includes('border-gray-300')) return '#D1D5DB';
     return '#BFDBFE'; // Default
}

/**
 * Maps Tailwind text color classes to hex/rgb values for Canvas.
 * @param {string} twClass - Tailwind class string.
 * @returns {string} - CSS color value or a default.
 */
export function mapTailwindTextToColor(twClass) {
    if (!twClass) return '#1E40AF'; // Default text
    if (twClass.includes('text-blue-900')) return '#1E3A8A';
    if (twClass.includes('text-red-900')) return '#7F1D1D';
    if (twClass.includes('text-indigo-900')) return '#312E81';
    if (twClass.includes('text-teal-900')) return '#0F766E';
    if (twClass.includes('text-pink-900')) return '#831843';
    if (twClass.includes('text-cyan-900')) return '#155E75';
    if (twClass.includes('text-lime-900')) return '#3F6212';
    if (twClass.includes('text-fuchsia-900')) return '#701A75';
    if (twClass.includes('text-green-900')) return '#064E3B';
    if (twClass.includes('text-yellow-900')) return '#713F12';
    if (twClass.includes('text-purple-900')) return '#5B21B6';
    if (twClass.includes('text-orange-900')) return '#7C2D12';
    if (twClass.includes('text-gray-900')) return '#111827';
    return '#1E40AF'; // Default
}

/**
 * Brightens a hex color by a specified amount.
 * @param {string} hex - Hex color string (e.g., '#FF0000' or 'FF0000').
 * @param {number} amount - Amount to brighten (0-1, where 1 is maximum brightness).
 * @returns {string} - Brightened hex color string.
 */
export function brightenHexColor(hex, amount = 0.5) {
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Parse RGB values
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Brighten by mixing with white
    const brightenedR = Math.round(r + (255 - r) * amount);
    const brightenedG = Math.round(g + (255 - g) * amount);
    const brightenedB = Math.round(b + (255 - b) * amount);
    
    // Convert back to hex
    const toHex = (n) => {
        const hex = Math.max(0, Math.min(255, n)).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    };
    
    return `#${toHex(brightenedR)}${toHex(brightenedG)}${toHex(brightenedB)}`;
}